import { FormEvent, useMemo, useState } from "react";
import { ArrowRight, Building2, ChevronLeft, Phone, User, Users } from "lucide-react";
import type { AuthMeDTO } from "@tendvia/shared";
import { verticalKinds } from "@tendvia/shared";
import { api } from "../api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";

type ProfileSetupScreenProps = {
  me: AuthMeDTO;
  onSaved: (me: AuthMeDTO) => void;
};

const verticalLabels: Record<string, string> = {
  auto_detailing: "Auto detailing",
  pressure_washing: "Pressure washing",
  junk_removal: "Junk removal",
  pool_cleaning: "Pool cleaning",
  airbnb_turnover: "Airbnb turnover",
  handyman: "Handyman",
  general: "General services"
};

function labelForVertical(vertical: string) {
  return verticalLabels[vertical] ?? vertical.replaceAll("_", " ");
}

export function ProfileSetupScreen({ me, onSaved }: ProfileSetupScreenProps) {
  const [step, setStep] = useState(me.user.name ? 1 : 0);
  const [ownerName, setOwnerName] = useState(me.user.name ?? "");
  const [businessName, setBusinessName] = useState(me.organization.name);
  const [vertical, setVertical] = useState(me.organization.vertical);
  const [phone, setPhone] = useState(me.organization.phone ?? "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const isOwnerStep = step === 0;
  const stepLabel = useMemo(() => (isOwnerStep ? "Step 1 of 2" : "Step 2 of 2"), [isOwnerStep]);

  async function saveOwner(event: FormEvent) {
    event.preventDefault();
    const trimmedName = ownerName.trim();
    if (!trimmedName) return;

    setBusy(true);
    setMessage("");
    try {
      const user = await api.updateProfile(trimmedName);
      setOwnerName(user.name ?? trimmedName);
      setStep(1);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save your profile.");
    } finally {
      setBusy(false);
    }
  }

  async function finishSetup(event: FormEvent) {
    event.preventDefault();

    const trimmedBusinessName = businessName.trim();
    if (!trimmedBusinessName) return;

    setBusy(true);
    setMessage("");
    try {
      const organization = await api.updateOrg({
        name: trimmedBusinessName,
        vertical,
        phone: phone.trim() || null
      });
      onSaved({
        ...me,
        user: {
          ...me.user,
          name: ownerName.trim() || me.user.name
        },
        organization
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save your workspace.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-background px-4 py-8 text-text">
      <section className="w-full max-w-2xl overflow-hidden rounded-panel border border-border/60 bg-surface/95 p-6 shadow-soft">
        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="inline-grid size-12 place-items-center rounded-token border border-border/40 bg-surface-soft text-primary">
              <Building2 className="size-5" aria-hidden="true" />
            </div>
            <span className="rounded-pill border border-border/60 bg-surface-soft px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-primary">
              {stepLabel}
            </span>
          </div>
          <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-accent">Business onboarding</p>
          <h1 className="mt-2 text-[2rem] font-black leading-tight tracking-[-0.04em] text-text">
            {isOwnerStep ? "Who is setting this up?" : "Tell us about the business"}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-6 text-muted">
            We use this to name the workspace, seed the right defaults, and get you to the first usable day fast.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs font-bold uppercase tracking-[0.12em] text-muted">
            <span className="inline-flex items-center gap-2 rounded-pill border border-border/60 bg-surface-soft px-3 py-2">
              <User className="size-3.5" aria-hidden="true" />
              Owner identity
            </span>
            <span className="inline-flex items-center gap-2 rounded-pill border border-border/60 bg-surface-soft px-3 py-2">
              <Building2 className="size-3.5" aria-hidden="true" />
              Business defaults
            </span>
            <span className="inline-flex items-center gap-2 rounded-pill border border-border/60 bg-surface-soft px-3 py-2">
              <Users className="size-3.5" aria-hidden="true" />
              Invite employees later
            </span>
          </div>
        </div>

        <div className="mb-6 rounded-panel border border-border/60 bg-surface-soft p-4">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">
            {isOwnerStep ? "Set the owner profile first" : "Set the workspace defaults next"}
          </p>
          <p className="mt-1 text-sm leading-6 text-muted">
            {isOwnerStep
              ? "This is the name customers and teammates will see inside TendVia."
              : "We will use this to label the workspace and contact settings."}
          </p>
        </div>

        {isOwnerStep ? (
          <form className="grid gap-4" onSubmit={saveOwner}>
            <Field
              label="Your name"
              value={ownerName}
              onChange={(event) => setOwnerName(event.target.value)}
              autoComplete="name"
              placeholder="Alex Rivera"
              required
            />

            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted">We’ll save your profile first, then move to the business setup.</p>
              <Button icon={<ArrowRight className="size-4" aria-hidden="true" />} disabled={busy || !ownerName.trim()}>
                {busy ? "Saving" : "Continue"}
              </Button>
            </div>
          </form>
        ) : (
          <form className="grid gap-4" onSubmit={finishSetup}>
            <Field
              label="Business name"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              autoComplete="organization"
              placeholder="Rivera Auto Detailing"
              required
            />

            <label className="grid gap-2 text-sm font-medium text-text">
              <span>Business type</span>
              <select
                className="min-h-12 rounded-token border border-border/70 bg-surface px-3 py-2 text-base text-text placeholder:text-muted"
                value={vertical}
                onChange={(event) => setVertical(event.target.value)}
              >
                {verticalKinds.map((kind) => (
                  <option key={kind} value={kind}>
                    {labelForVertical(kind)}
                  </option>
                ))}
              </select>
              <span className="text-xs font-normal text-muted">This picks the first workflow template for your workspace.</span>
            </label>

            <Field
              label="Business phone"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              autoComplete="tel"
              inputMode="tel"
              placeholder="352-555-0100"
              hint="Optional for onboarding, but useful for customer follow-ups."
            />

            <div className="rounded-panel border border-border/60 bg-surface-soft p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-grid size-9 shrink-0 place-items-center rounded-token border border-border/40 bg-surface text-primary">
                  <Phone className="size-4" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-text">What happens next</p>
                  <p className="mt-1 text-sm leading-6 text-muted">
                    TendVia will mark this workspace as ready, then you can add clients, quotes, jobs, and employees.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="secondary"
                icon={<ChevronLeft className="size-4" aria-hidden="true" />}
                disabled={busy}
                onClick={() => setStep(0)}
              >
                Back
              </Button>
              <Button icon={<ArrowRight className="size-4" aria-hidden="true" />} disabled={busy || !businessName.trim()}>
                {busy ? "Finishing" : "Create workspace"}
              </Button>
            </div>
          </form>
        )}

        {message ? <p className="mt-4 rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{message}</p> : null}
      </section>
    </main>
  );
}
